import { jsx as _jsx } from "react/jsx-runtime";
import deepmerge from 'deepmerge';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { FaRocket } from '@react-icons/all-files/fa/FaRocket';
import { jaenAgent, resetState, store, useAppDispatch, useAppSelector, withRedux } from '../redux';
import { actions as remoteActions } from '../redux/slices/remote';
import { discardDraft as discardSharedDraft, fetchDiscardPreview, publishSite, warmAuth } from '../clients/agent';
import { useSharedDraft } from '../hooks/use-shared-draft';
import { actions as pageActions } from '../redux/slices/page';
import * as statusActions from '../redux/slices/status';
import { actions as siteActions } from '../redux/slices/site';
import * as widgetActions from '../redux/slices/widget';
import { deepmergeArrayIdMerge } from '../utils/deepmerge';
import { useSiteMetadataContext } from './site-metadata';
import { uploadFile } from '../utils/open-storage-gateway';
import { useNotificationsContext } from './notifications';
import { sqJaen } from '../clients/jaen/src';
import { useAuth } from './auth';
export class DuplicateSlugError extends Error {
    constructor(slug) {
        super(`Could not add page with slug ${slug} as it is not unique`);
    }
}
const CMSManagementContext = createContext({
    templates: [],
    templatesForPage: () => [],
    siteMetadata: {},
    updateSiteMetadata: () => { },
    page: function () {
        throw new Error('Function not implemented.');
    },
    usePage: function () {
        throw new Error('Function not implemented.');
    },
    pages: () => [],
    tree: [],
    pagePath: () => '',
    isEditing: false,
    isPublishing: false,
    setIsPublishing: () => { },
    addPage: () => '',
    removePage: () => { },
    updatePage: () => { },
    clonePage: () => { },
    setIsEditing: () => { },
    draft: {
        save: () => { },
        import: () => Promise.resolve(),
        discard: () => Promise.resolve(null),
        discardPreview: () => Promise.resolve(null),
        publish: () => { }
    },
    sharedDraft: {
        enabled: false,
        saveState: 'idle',
        pending: 0,
        authors: {},
        hasUnpublished: false,
        connection: 'poll'
    }
});
export const CMSManagementProvider = withRedux(({ staticPages, children, templates }) => {
    const auth = useAuth();
    const dispatch = useAppDispatch();
    const notification = useNotificationsContext();
    const sharedDraft = useSharedDraft();
    useEffect(() => {
        if (!jaenAgent)
            return;
        dispatch(remoteActions.setActive(true));
        void warmAuth(jaenAgent);
        return () => {
            dispatch(remoteActions.setActive(false));
        };
    }, [dispatch]);
    const siteMetadata = useSiteMetadataContext();
    const updateSiteMetadata = useCallback((siteMetadata) => {
        dispatch(siteActions.updateSiteMetadata(siteMetadata));
    }, [dispatch]);
    const isEditing = useAppSelector(state => state.status.isEditing);
    const setIsEditing = (flag) => {
        dispatch(statusActions.setIsEditing(flag));
    };
    const isPublishing = useAppSelector(state => state.status.isPublishing);
    const setIsPublishing = (flag) => {
        dispatch(statusActions.setIsPublishing(flag));
    };
    const dynamicPagesDict = useAppSelector(state => state.page.pages.nodes);
    const pagesDict = useMemo(() => {
        const mergePages = (target, source) => {
            return deepmerge(target, source, {
                arrayMerge: deepmergeArrayIdMerge
            });
        };
        const filterDeletedPages = (pageDict) => {
            const cleanDict = {};
            for (const [pageId, page] of Object.entries(pageDict)) {
                if (!page.deleted) {
                    cleanDict[pageId] = { ...page };
                    if (page.childPages) {
                        const validChildren = page.childPages.filter(child => {
                            return !child.deleted && !pageDict[child.id]?.deleted;
                        });
                        cleanDict[pageId].childPages = validChildren;
                    }
                }
            }
            return cleanDict;
        };
        const createPagesDictionary = (dynamicPagesDict, staticPages) => {
            const combinedPagesDict = { ...dynamicPagesDict };
            for (const staticPage of staticPages) {
                if (combinedPagesDict[staticPage.id]) {
                    combinedPagesDict[staticPage.id] = mergePages(staticPage, combinedPagesDict[staticPage.id] || {});
                }
                else {
                    combinedPagesDict[staticPage.id] = staticPage;
                }
            }
            return filterDeletedPages(combinedPagesDict);
        };
        return createPagesDictionary(dynamicPagesDict, staticPages);
    }, [dynamicPagesDict, staticPages]);
    const pages = useCallback((parentId) => {
        const valuesWithIds = Object.entries(pagesDict).map(([key, value]) => ({
            id: key,
            ...value
        }));
        if (parentId) {
            const parentPage = valuesWithIds.find(page => page.id === parentId);
            if (parentPage) {
                const childPages = parentPage.childPages || [];
                const sortedChildPages = [...childPages];
                sortedChildPages.sort((a, b) => {
                    if (!parentPage.childPagesOrder) {
                        return 5;
                    }
                    const aIndex = parentPage.childPagesOrder?.indexOf(a.id);
                    const bIndex = parentPage.childPagesOrder?.indexOf(b.id);
                    return aIndex - bIndex;
                });
                return sortedChildPages.map(child => {
                    const found = valuesWithIds.find(page => page.id === child.id);
                    if (!found) {
                        throw new Error(`Could not find page with id ${child.id} in pagesDict`);
                    }
                    return found;
                });
            }
        }
        return Object.values(valuesWithIds);
    }, [pagesDict]);
    const page = useCallback((pageId = 'JaenPage /') => {
        const found = pages().find(p => p.id === pageId);
        if (!found) {
            throw new Error(`Could not find page with id ${pageId}`);
        }
        return found;
    }, [pages]);
    const usePage = useCallback((pageId = 'JaenPage /') => {
        const found = pages().find(p => p.id === pageId);
        if (!found) {
            throw new Error(`Could not find page with id ${pageId}`);
        }
        return found;
    }, [pages]);
    const addPage = (page) => {
        const slug = page.slug || 'new-page';
        const isSlugDuplicate = pages(page.parentPage?.id).some(page => page.slug === slug);
        if (isSlugDuplicate) {
            throw new DuplicateSlugError(slug);
        }
        page.createdBy = auth.user?.profile?.sub || 'unknown';
        dispatch(pageActions.page_updateOrCreate(page));
        return store.getState().page.pages.lastAddedNodeId;
    };
    const updatePage = (pageId, updatedPage) => {
        const page = pagesDict[pageId];
        const slug = updatedPage.slug || 'new-page';
        const parentPage = updatedPage.parentPage || page?.parentPage;
        const isSlugDuplicate = pages(parentPage?.id).some(page => page.slug === slug && page.id !== pageId);
        if (isSlugDuplicate) {
            throw new DuplicateSlugError(slug);
        }
        const fromId = updatedPage?.parentPage?.id !== undefined &&
            page?.parentPage?.id !== updatedPage?.parentPage?.id
            ? page?.parentPage?.id
            : undefined;
        dispatch(pageActions.page_updateOrCreate({
            id: pageId,
            ...updatedPage,
            fromId: fromId
        }));
    };
    const removePage = (pageId) => {
        const deletePage = (pageId) => {
            const page = pagesDict[pageId];
            if (!page) {
                throw new Error(`Could not find page with id ${pageId}`);
            }
            dispatch(pageActions.page_markForDeletion(pageId));
            if (page.childPages) {
                for (const child of page.childPages) {
                    deletePage(child.id);
                }
            }
        };
        deletePage(pageId);
    };
    const clonePage = (pageId, slug) => {
        const page = pagesDict[pageId];
        if (!page) {
            throw new Error(`Could not find page with id ${pageId}`);
        }
        const newPage = {
            ...page,
            id: undefined,
            slug: slug || page.slug + '-copy',
            jaenPageMetadata: {
                ...page.jaenPageMetadata,
                title: page.jaenPageMetadata?.title + ' (copy)'
            }
        };
        const newPageId = addPage(newPage);
        if (page.childPages) {
            for (const child of page.childPages) {
                clonePage(child.id, child.slug);
            }
        }
        return newPageId;
    };
    const tree = useMemo(() => {
        const tree = [];
        const buildTree = (parentId) => {
            const children = pages(parentId);
            return children.map(child => ({
                id: child.id,
                label: child.jaenPageMetadata.title || child.slug,
                showInNodeGraphVisualizer: child.pageConfig?.showInNodeGraphVisualizer ?? true,
                children: buildTree(child.id),
                excludedFromIndex: !!child.excludedFromIndex
            }));
        };
        const root = page('JaenPage /');
        tree.push({
            id: root.id,
            label: root.jaenPageMetadata.title || root.slug,
            showInNodeGraphVisualizer: root.pageConfig?.showInNodeGraphVisualizer ?? true,
            children: buildTree(root.id),
            excludedFromIndex: !!root.excludedFromIndex
        });
        return tree;
    }, [pages]);
    const pagePath = useCallback((pageId) => {
        const page = pagesDict[pageId];
        if (!page) {
            throw new Error(`Could not find page with id ${pageId}`);
        }
        if (page.slug === 'root')
            return '/';
        const pathParts = [page.slug];
        let parent = pagesDict[page.parentPage?.id || ''];
        while (parent && parent.slug !== 'root') {
            pathParts.unshift(parent.slug);
            parent = pagesDict[parent.parentPage?.id || ''];
        }
        const path = pathParts.join('/');
        return path.startsWith('/') ? path : '/' + path;
    }, [pagesDict]);
    const templatesForPage = useCallback((pageId) => {
        const page = pagesDict[pageId];
        if (!page) {
            throw new Error(`Could not find page with id ${pageId}`);
        }
        if (page.template) {
            return (templates.find(template => template.id === page.template)
                ?.childTemplates || []);
        }
        if (page.pageConfig?.childTemplates) {
            const childTemplates = [];
            for (const childTemplateId of page.pageConfig?.childTemplates) {
                const childTemplate = templates.find(template => template.id === childTemplateId);
                if (childTemplate) {
                    childTemplates.push(childTemplate);
                }
            }
            return childTemplates;
        }
        return [];
    }, [pagesDict, templates]);
    const saveDraft = useCallback(() => {
        const state = store.getState();
        const draftId = Date.now();
        const blob = new Blob([JSON.stringify({ draftId, state })], {
            type: 'application/json'
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `jaen-draft-${draftId}.json`;
        a.click();
        a.remove();
    }, []);
    const importDraft = useCallback(async () => {
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'application/json';
            input.onchange = () => {
                const file = input.files?.item(0);
                if (!file)
                    return;
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const content = JSON.parse(reader.result);
                        const { draftId, state } = content;
                        const regex = /^jaen-draft-(\d+)\.json$/;
                        const match = file.name.match(regex);
                        if (match && match[1] === draftId.toString()) {
                            resetState(state);
                            resolve();
                        }
                        else {
                            reject('Invalid draft file. Please select a valid draft file.');
                        }
                    }
                    catch (error) {
                        console.error(error);
                        reject('Error parsing draft file. Please select a valid draft file.');
                    }
                };
                reader.readAsText(file);
            };
            input.click();
        });
    }, []);
    const discardPreview = useCallback(async () => {
        if (!jaenAgent)
            return null;
        return await fetchDiscardPreview(jaenAgent);
    }, []);
    const discardDraft = useCallback(async (atRevision) => {
        if (jaenAgent) {
            const answer = await discardSharedDraft(jaenAgent, atRevision);
            if (answer.discarded) {
                setIsPublishing(false);
            }
            return answer;
        }
        dispatch(pageActions.discardAllChanges());
        dispatch(siteActions.discardAllChanges());
        dispatch(widgetActions.discardAllChanges());
        setIsEditing(false);
        setIsPublishing(false);
        return null;
    }, [setIsEditing, setIsPublishing]);
    const publishDraft = useCallback(async () => {
        if (jaenAgent) {
            try {
                const message = await notification.prompt({
                    icon: FaRocket,
                    title: 'Publish',
                    message: 'Publish the shared draft? Everything saved becomes one migration and the site is rebuilt.',
                    confirmText: 'Publish',
                    cancelText: 'Cancel'
                }, `Update ${Object.values(store.getState().page.pages.nodes)
                    .length} pages`);
                if (!message)
                    return;
                const answer = await publishSite(jaenAgent, message);
                if (answer.published) {
                    dispatch(remoteActions.publishQueued({
                        revision: answer.publishedRevision ?? answer.revision ?? undefined
                    }));
                }
                if (answer.queued) {
                    setIsPublishing(true);
                }
                if (answer.published && answer.queued) {
                    notification.toast({
                        status: 'success',
                        title: 'Publish',
                        description: answer.runUrl
                            ? `Published, and the build was started: ${answer.runUrl}`
                            : 'Published, and the build was started'
                    });
                }
                else if (answer.published) {
                    notification.toast({
                        status: 'success',
                        title: 'Publish',
                        description: answer.reason ||
                            'Published. This site has no automatic build; the operator builds it.'
                    });
                }
                else if (answer.queued) {
                    notification.toast({
                        status: 'info',
                        title: 'Publish',
                        description: answer.reason ||
                            'Nothing new to publish; the build was started.'
                    });
                }
                else {
                    notification.toast({
                        status: 'info',
                        title: 'Publish',
                        description: answer.reason || 'Nothing was published.'
                    });
                }
            }
            catch (error) {
                console.error('An error occurred:', error);
                notification.toast({
                    status: 'error',
                    title: 'Publish',
                    description: error.message
                });
            }
            return;
        }
        try {
            const state = store.getState();
            const message = await notification.prompt({
                icon: FaRocket,
                title: 'Publishing',
                message: 'Enter a message for the publish commit',
                confirmText: 'Publish',
                cancelText: 'Cancel'
            }, `Update ${Object.values(state.page.pages.nodes).length} pages`);
            if (message) {
                const migrationData = {
                    message,
                    createdAt: new Date().toISOString(),
                    data: {
                        pages: Object.entries(state.page.pages.nodes).map(([id, page]) => ({
                            id,
                            ...page
                        })),
                        site: state.site,
                        widgets: state.widget.nodes
                    }
                };
                const uploadedMigration = await uploadFile(migrationData, `migrations-${Date.now()}.json`);
                if (uploadedMigration) {
                    const [_, errors] = await sqJaen.mutate(m => m.publish({
                        config: {
                            repository: __JAEN_REMOTE__.repository,
                            repositoryCwd: __JAEN_REMOTE__.cwd
                        },
                        migrationURL: uploadedMigration.fileUrl
                    }));
                    if (errors) {
                        console.error(errors);
                        notification.toast({
                            status: 'error',
                            title: 'Migration',
                            description: 'An error occurred during migration publishing'
                        });
                    }
                    else {
                        notification.toast({
                            status: 'success',
                            title: 'Migration',
                            description: 'Migration published successfully'
                        });
                        setIsPublishing(true);
                    }
                }
                else {
                    notification.toast({
                        status: 'error',
                        title: 'Migration',
                        description: 'Migration file could not be uploaded'
                    });
                }
            }
        }
        catch (error) {
            console.error('An error occurred:', error);
            notification.toast({
                status: 'error',
                title: 'Error',
                description: 'An unexpected error occurred'
            });
        }
    }, [dispatch]);
    return (_jsx(CMSManagementContext.Provider, { value: {
            templates,
            siteMetadata,
            updateSiteMetadata,
            page,
            usePage,
            pages,
            templatesForPage,
            tree,
            pagePath,
            isEditing,
            isPublishing,
            setIsPublishing,
            addPage,
            removePage,
            updatePage,
            clonePage,
            setIsEditing,
            draft: {
                save: saveDraft,
                import: importDraft,
                discard: discardDraft,
                discardPreview,
                publish: publishDraft
            },
            sharedDraft
        }, children: children }));
});
export function useCMSManagementContext() {
    return useContext(CMSManagementContext);
}
